/**
 * @fileoverview Deliverable submission drawer (student). Owns the load/submit
 * lifecycle; renders the presentational section in the scrollable body.
 * There is NO submit button: the legacy upload widget auto-submits via
 * `successUploadCallback` (AR-738) and the FIRST link added does the same
 * (AR-678, same PUT + auto-close flow). On a successful submit we warn the
 * user and auto-close after 5s (defensive timer cleared on unmount/close).
 * When a done delivery already exists (edit mode, AR-741) uploads/removals hit
 * that same delivery and we skip the re-PUT/auto-close: just toast + drop caches
 * (links: the data source invalidates the cache on add/remove).
 * Endpoints: GET /api/student_tasks/:id, PUT /api/student_task_deliveries/:id,
 * POST/DELETE /api/student_task_deliveries/:id/links.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'
import { StudentTaskDrawerShell } from '../components'
import { dataSource } from '../dataSource'
import { useStudentDeliverable } from '../hooks/useStudentDeliverable'
import type { AddLinkOutcome } from './linkUtils'
import { StudentDeliverableSection } from './StudentDeliverableSection'

interface StudentDeliverableDrawerProps {
  opened: boolean
  identifierId: string
  onClose: () => void
  onSubmitted?: () => void
}

const AUTO_CLOSE_DELAY_MS = 5000

export function StudentDeliverableDrawer({
  opened,
  identifierId,
  onClose,
  onSubmitted,
}: StudentDeliverableDrawerProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`student_tasks.deliverable_drawer.${key}`)
  const { loading, errored, submitted, data, links, submit, addLink, removeLink } =
    useStudentDeliverable(identifierId, opened)
  // Concurrent DELETEs: one spinner per link being removed.
  const [removingLinkIds, setRemovingLinkIds] = useState<ReadonlySet<string>>(new Set())

  // Auto-close timer kept in a ref so we can cancel it on unmount/close and never
  // call onSubmitted/onClose (which run setState upstream) after the drawer is gone.
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current)
      autoCloseTimer.current = null
    }
  }, [])

  // Cancel any pending auto-close when the drawer unmounts or closes.
  useEffect(() => clearAutoCloseTimer, [clearAutoCloseTimer])
  useEffect(() => {
    if (!opened) clearAutoCloseTimer()
  }, [opened, clearAutoCloseTimer])

  // First-submission flow, shared by files and links: auto-submit with the
  // default description — the free-text answer field was removed (AR-780) and
  // the backend requires a non-empty description — then warn the user and
  // schedule the auto-close. Single timer owner — never duplicated.
  const submitAndScheduleClose = useCallback(async () => {
    const ok = await submit(tr('default_description'))
    notifications.show({
      message: ok ? tr('auto_submit_success') : tr('submit_error'),
      color: ok ? 'green' : 'red',
      icon: ok ? <IconCheck size={18} /> : <IconX size={18} />,
    })
    if (!ok) return

    clearAutoCloseTimer()
    autoCloseTimer.current = setTimeout(() => {
      autoCloseTimer.current = null
      onSubmitted?.()
      onClose()
    }, AUTO_CLOSE_DELAY_MS)
  }, [submit, onSubmitted, onClose, clearAutoCloseTimer, tr])

  // Fired by the legacy FileUpload (via successUploadCallback) after an upload
  // OR a delete finishes. Edit mode (`submitted` also covers a submit that just
  // happened in this session, before the reload lands): the files changed on
  // that same delivery, so there is no re-PUT and no auto-close — just notify
  // and drop the stale caches (AR-741).
  const handleAutoSubmit = useCallback(async () => {
    if (submitted) {
      clearAutoCloseTimer()
      dataSource.invalidateDeliverable(identifierId)
      notifications.show({
        message: tr('files_updated'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
      return
    }

    await submitAndScheduleClose()
  }, [submitted, identifierId, submitAndScheduleClose, clearAutoCloseTimer, tr])

  // Add link (AR-678): POST via hook; success → green toast and, when the
  // delivery was not submitted yet, the SAME auto-submit flow as files.
  // 422 → the modal shows the inline error; other failures → red toast.
  const handleAddLink = useCallback(
    async (url: string): Promise<AddLinkOutcome> => {
      const outcome = await addLink(url)
      if (outcome === 'success') {
        notifications.show({
          message: tr('link_added'),
          color: 'green',
          icon: <IconCheck size={18} />,
        })
        if (!submitted) await submitAndScheduleClose()
      } else if (outcome === 'error') {
        notifications.show({
          message: tr('link_add_error'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
      return outcome
    },
    [addLink, submitted, submitAndScheduleClose, tr],
  )

  const handleRemoveLink = useCallback(
    async (linkId: string) => {
      setRemovingLinkIds(current => new Set(current).add(linkId))
      const ok = await removeLink(linkId)
      setRemovingLinkIds(current => {
        const next = new Set(current)
        next.delete(linkId)
        return next
      })
      if (!ok) {
        notifications.show({
          message: tr('link_remove_error'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [removeLink, tr],
  )

  return (
    <StudentTaskDrawerShell
      opened={opened}
      title={tr('submit_title')}
      onClose={onClose}
      footer={undefined}
    >
      <StudentDeliverableSection
        loading={loading}
        errored={errored}
        data={data}
        links={links}
        removingLinkIds={removingLinkIds}
        onAutoSubmit={handleAutoSubmit}
        onAddLink={handleAddLink}
        onRemoveLink={handleRemoveLink}
      />
    </StudentTaskDrawerShell>
  )
}
